/**
 * Document Converter - Convert DOCX/XLSX to text for Gemini processing
 * SLAM Sports AI - College Recruiter Analysis System
 *
 * Gemini 2.5 Pro natively supports PDF and plain text, but requires
 * DOCX/XLSX to be converted to text format before processing.
 *
 * @module documentConverter
 * Ported from theWIRE/SLAMPitchDashboard/lib/documentConverter.ts
 */

import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import {
  ACCEPTED_DOCUMENT_TYPES,
  ACCEPTED_XML_TYPES,
  CONVERTIBLE_DOCUMENT_TYPES,
  ALL_CONTEXT_TYPES,
  CONTEXT_FILE_EXTENSIONS,
} from './constants';

// Re-export constants for backwards compatibility
export {
  ACCEPTED_DOCUMENT_TYPES,
  ACCEPTED_XML_TYPES,
  CONVERTIBLE_DOCUMENT_TYPES,
  ALL_CONTEXT_TYPES,
  CONTEXT_FILE_EXTENSIONS,
};

/**
 * @typedef {Object} ConversionResult
 * @property {boolean} success - Whether conversion succeeded
 * @property {string} [text] - Converted text content
 * @property {string} [error] - Error message if failed
 * @property {boolean} [isNative] - True if file type is native to Gemini
 */

/**
 * Convert document file to text content
 * @param {File} file - The file to convert
 * @returns {Promise<ConversionResult>} Converted text content or error
 */
export async function convertDocumentToText(file) {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const mimeType = file.type;

  try {
    // Check if it's a Gemini-native type (PDF, TXT, XML)
    if (ACCEPTED_DOCUMENT_TYPES.includes(mimeType) || extension === 'pdf') {
      if (extension === 'pdf' || mimeType === 'application/pdf') {
        return {
          success: true,
          text: '[PDF Document - Will be processed natively by Gemini]',
          isNative: true,
        };
      }
      if (extension === 'txt' || mimeType === 'text/plain') {
        const text = await file.text();
        return { success: true, text, isNative: true };
      }
    }

    // Handle XML files (Gemini native support)
    if (ACCEPTED_XML_TYPES.includes(mimeType) || extension === 'xml') {
      const text = await file.text();
      return {
        success: true,
        text: `[XML DOCUMENT: ${file.name}]\n---\n${text}\n---`,
        isNative: true,
      };
    }

    // Handle convertible types
    switch (extension) {
      case 'docx':
        return await convertDocxToText(file);
      case 'doc':
        return await convertDocToText(file);
      case 'xlsx':
        return await convertXlsxToText(file);
      case 'xls':
        return await convertXlsToText(file);
      default:
        return {
          success: false,
          error: `Unsupported document type: ${extension}`,
        };
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown conversion error',
    };
  }
}

/**
 * Convert DOCX to text using mammoth
 * @param {File} file - DOCX file
 * @returns {Promise<ConversionResult>}
 */
async function convertDocxToText(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });

    if (result.value) {
      return {
        success: true,
        text: formatDocumentText(result.value, file.name),
      };
    }

    return {
      success: false,
      error: 'No text content extracted from DOCX',
    };
  } catch (error) {
    return {
      success: false,
      error: `DOCX conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Convert legacy DOC to text (limited support)
 * @param {File} file - DOC file
 * @returns {Promise<ConversionResult>}
 */
async function convertDocToText(file) {
  try {
    // mammoth has limited DOC support - attempt conversion
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });

    if (result.value) {
      return {
        success: true,
        text: formatDocumentText(result.value, file.name),
      };
    }

    return {
      success: false,
      error: 'Legacy DOC format may not be fully supported. Consider converting to DOCX.',
    };
  } catch {
    return {
      success: false,
      error: 'Legacy DOC format not supported. Please convert to DOCX or PDF.',
    };
  }
}

/**
 * Convert XLSX to text using xlsx library
 * @param {File} file - XLSX file
 * @returns {Promise<ConversionResult>}
 */
async function convertXlsxToText(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    let text = `[SPREADSHEET DOCUMENT: ${file.name}]\n`;
    text += `Sheets: ${workbook.SheetNames.join(', ')}\n\n`;

    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      text += `=== Sheet: ${sheetName} ===\n`;

      // Convert to CSV format for better Gemini parsing
      const csv = XLSX.utils.sheet_to_csv(sheet);
      text += csv;
      text += '\n\n';
    });

    return {
      success: true,
      text: text.trim(),
    };
  } catch (error) {
    return {
      success: false,
      error: `XLSX conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Convert legacy XLS to text (limited support)
 * @param {File} file - XLS file
 * @returns {Promise<ConversionResult>}
 */
async function convertXlsToText(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    let text = `[SPREADSHEET DOCUMENT: ${file.name}]\n`;
    text += `Sheets: ${workbook.SheetNames.join(', ')}\n\n`;

    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      text += `=== Sheet: ${sheetName} ===\n`;
      text += XLSX.utils.sheet_to_csv(sheet);
      text += '\n\n';
    });

    return {
      success: true,
      text: text.trim(),
    };
  } catch {
    return {
      success: false,
      error: 'Legacy XLS format conversion failed. Please convert to XLSX.',
    };
  }
}

/**
 * Format extracted document text with metadata header
 * @param {string} text - Raw text content
 * @param {string} filename - Source filename
 * @returns {string} Formatted document text
 */
function formatDocumentText(text, filename) {
  return `[DOCUMENT: ${filename}]\n---\n${text.trim()}\n---`;
}

/**
 * Categorize context file based on content analysis
 * @param {string} filename - File name
 * @param {string} content - File content
 * @returns {'scouting' | 'biomechanics' | 'statistics' | 'general'} Category
 */
export function categorizeContextFile(filename, content) {
  const combined = (filename + ' ' + content).toLowerCase();

  // Scouting reports
  if (
    combined.includes('scout') ||
    combined.includes('advance') ||
    combined.includes('opponent') ||
    combined.includes('report') ||
    combined.includes('game notes')
  ) {
    return 'scouting';
  }

  // Biomechanics studies
  if (
    combined.includes('biomechanics') ||
    combined.includes('kinematic') ||
    combined.includes('motion') ||
    combined.includes('kinatrax') ||
    combined.includes('skeletal') ||
    combined.includes('trunk tilt') ||
    combined.includes('arm slot')
  ) {
    return 'biomechanics';
  }

  // Statistical data
  if (
    combined.includes('statcast') ||
    combined.includes('statistics') ||
    combined.includes('analytics') ||
    combined.includes('run value') ||
    combined.includes('pitch type') ||
    combined.includes('whiff') ||
    combined.includes('xwoba')
  ) {
    return 'statistics';
  }

  return 'general';
}

/**
 * Check if a file is a context document type
 * @param {File} file - File to check
 * @returns {boolean} True if file is a context document type
 */
export function isContextFileType(file) {
  const mimeType = file.type;
  const extension = file.name.split('.').pop()?.toLowerCase();

  return (
    ACCEPTED_DOCUMENT_TYPES.includes(mimeType) ||
    ACCEPTED_XML_TYPES.includes(mimeType) ||
    CONVERTIBLE_DOCUMENT_TYPES.includes(mimeType) ||
    ['pdf', 'txt', 'docx', 'xlsx', 'doc', 'xls', 'xml'].includes(extension || '')
  );
}

/**
 * Get file type category for display
 * @param {File} file - File to get label for
 * @returns {string} Human-readable file type label
 */
export function getDocumentTypeLabel(file) {
  const extension = file.name.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'PDF Document';
    case 'txt':
      return 'Text File';
    case 'docx':
    case 'doc':
      return 'Word Document';
    case 'xlsx':
    case 'xls':
      return 'Spreadsheet';
    case 'xml':
      return 'XML Document';
    default:
      return 'Document';
  }
}

export default {
  convertDocumentToText,
  categorizeContextFile,
  isContextFileType,
  getDocumentTypeLabel,
  ACCEPTED_DOCUMENT_TYPES,
  ACCEPTED_XML_TYPES,
  CONVERTIBLE_DOCUMENT_TYPES,
  ALL_CONTEXT_TYPES,
  CONTEXT_FILE_EXTENSIONS,
};
